/**
 * Phase 175-C — Player Hero Card (branded placeholder above AI Insights)
 *
 * Renders a high-end "Target Subject Analysis" tile for individual scouting
 * reports. This is a pure HTML/CSS card — no CV / OpenCV / YOLOv8 — designed
 * as a brand-tier placeholder until a real frame-extraction pipeline lands.
 *
 * Mounted only when the parent report has `focus_type === 'Player'`.
 */
import { Trophy, Activity, Target, Eye } from 'lucide-react';

export default function PlayerHeroCard({
  jerseyNumber,
  academyName,
  sport,
  matchDate,
  opponent,
  status = 'Scouting Pool',
}) {
  const sportLabel = (sport || 'Football').replace(/^./, (c) => c.toUpperCase());
  const dateLabel = matchDate ? new Date(matchDate).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  }) : null;
  // Parse "#10 (Striker)" → main "#10", role "Striker"
  const jerseyRaw = (jerseyNumber || '—').toString();
  const jerseyMatch = jerseyRaw.match(/^#?(\S+?)(?:\s*\(([^)]+)\))?$/);
  const jerseyMain = jerseyMatch ? `#${jerseyMatch[1]}` : jerseyRaw.slice(0, 5);
  const jerseyRole = jerseyMatch && jerseyMatch[2] ? jerseyMatch[2] : null;

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 rounded-2xl p-5 sm:p-6 text-white shadow-xl border border-slate-800/80"
      data-testid="phase175-player-hero-card"
    >
      {/* Ambient glow */}
      <div className="absolute right-0 top-0 -mt-10 -mr-10 w-44 h-44 bg-emerald-500/15 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute left-0 bottom-0 -mb-10 -ml-10 w-32 h-32 bg-violet-500/10 rounded-full blur-3xl" aria-hidden="true" />

      {/* Subtle pitch grid texture */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none"
        viewBox="0 0 200 100" preserveAspectRatio="none" aria-hidden="true"
      >
        <defs>
          <pattern id="phase175-grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.3" />
          </pattern>
        </defs>
        <rect width="200" height="100" fill="url(#phase175-grid)" />
      </svg>

      <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-stretch justify-between gap-5">
        {/* Left — jersey + headline */}
        <div className="flex items-center gap-4 sm:gap-5">
          <div
            className="w-20 h-20 sm:w-24 sm:h-24 bg-slate-800/80 rounded-xl border border-slate-700 flex flex-col items-center justify-center relative shadow-inner shrink-0"
            data-testid="phase175-jersey-chip"
          >
            <span className="text-[9px] uppercase font-bold tracking-[0.18em] text-emerald-400 absolute top-2">
              Jersey
            </span>
            <span className="text-3xl sm:text-4xl font-black tracking-tighter mt-3 text-white tabular-nums leading-none">
              {jerseyMain}
            </span>
            {jerseyRole && (
              <span className="text-[8px] uppercase tracking-[0.2em] text-slate-400 mt-1 px-1 leading-tight text-center">
                {jerseyRole}
              </span>
            )}
          </div>
          <div className="space-y-1.5 text-center sm:text-left min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
              <Activity className="w-3 h-3" /> Elite Performance Profile
            </div>
            <h3 className="text-lg sm:text-xl font-black text-white tracking-tight leading-tight"
              data-testid="phase175-hero-headline">
              Target Subject Analysis
            </h3>
            <p className="text-[11px] text-slate-400 font-medium leading-snug">
              {academyName || 'Out-Play Academy'}
              <span className="mx-1.5 text-slate-600">·</span>
              {sportLabel}
              {dateLabel && (
                <>
                  <span className="mx-1.5 text-slate-600">·</span>
                  {dateLabel}
                </>
              )}
              {opponent && (
                <>
                  <span className="mx-1.5 text-slate-600">·</span>
                  vs {opponent}
                </>
              )}
            </p>
          </div>
        </div>

        {/* Right — telemetry pills */}
        <div
          className="flex items-stretch gap-0 bg-slate-950/50 p-2.5 rounded-xl border border-slate-800/70 w-full sm:w-auto"
          data-testid="phase175-telemetry-pills"
        >
          <div className="text-center px-4 sm:px-5 flex flex-col justify-center min-w-[88px]">
            <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em]">
              Status
            </span>
            <span className="text-[11px] font-extrabold text-amber-400 flex items-center gap-1 mt-1 justify-center">
              <Trophy className="w-3 h-3" /> {status}
            </span>
          </div>
          <div className="h-auto w-px bg-slate-800 mx-1" />
          <div className="text-center px-4 sm:px-5 flex flex-col justify-center min-w-[88px]">
            <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em]">
              Telemetry
            </span>
            <span className="text-[11px] font-extrabold text-emerald-400 flex items-center gap-1 mt-1 justify-center">
              <Target className="w-3 h-3" /> AI Active
            </span>
          </div>
          <div className="h-auto w-px bg-slate-800 mx-1" />
          <div className="text-center px-4 sm:px-5 flex flex-col justify-center min-w-[88px]">
            <span className="block text-[9px] font-bold text-slate-500 uppercase tracking-[0.15em]">
              Coverage
            </span>
            <span className="text-[11px] font-extrabold text-sky-400 flex items-center gap-1 mt-1 justify-center">
              <Eye className="w-3 h-3" /> Live frame
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
